import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FiCornerUpLeft, FiCornerUpRight, FiSave, FiLayers, FiActivity, FiCode, FiUploadCloud, FiPlay, FiSquare, FiLoader, FiLock, FiEye,
} from 'react-icons/fi';
import { be1Fetch } from '../../../config/api';
import { stopRun } from '../../../services/executionApi';
import { logError, logUsage } from '../../../services/eventLog';
import { showAlert, showChoice, showConfirm } from '../../common/AppDialog';
import { formatNumber } from '../../../utils/formatters';
import { connIdsOf, pushPcConnsFor } from '../../../services/pcConnections';
import RunHistoryDialog from '../RunHistoryDialog';
import RunParamsDialog from '../RunParamsDialog';
import VersionHistoryDialog from '../VersionHistoryDialog';
import DagCodeModal from '../DagCodeModal';
import type { DagOverrideInfo } from '../DagCodeModal';
import { extractParamRefs, normalizeSettings } from '../../../types/workflowSettings';
import store, { RootState, AppDispatch } from '../../../store';
import { saveWorkflowCanvas, setSavedSnapshot } from '../../../store/project/projectSlice';
import { CanvasDoc } from '../../../services/repo';
import { acquireEdit } from '../../../store/authSlice';
import { setRunStatus, setRunDurations, clearRunStatus, undo, redo, resetWorkflow, setCurrentWorkflow, setWorkflowSettings, requestFitView, updateNodeData, setSelectedNode } from '../../../store/workflow/workflowSlice';
import { computeNodeRunStatus, computeNodeDurations } from '../utils/runStatus';
import type { RunScope } from '../utils/runStatus';
import { isCanvasDirty, normalizeCanvas } from '../../../utils/canvasSnapshot';
import { CANVAS_SCHEMA_VERSION } from '../../../services/canvasSchema';
import type { SchemaInfo } from '../../../services/canvasSchema';
import { errorText } from '../../../services/errorText';
import { withTimeZone } from '../../../utils/timeZone';
import ToolbarButton, { ToolbarDivider } from './ToolbarButton';
import { changedSqlNodes, OPEN_SQL_CHECK_EVENT, rememberResult, runSqlCheck, skippedCheck, toNodeCheck, type CheckResult } from '../../../services/sqlCheck';

/**
 * 실행을 지켜보는 상한 — 이 시간이 지나면 화면이 보기를 그만둔다(실행은 서버에서 계속된다).
 * 3분이던 때에는 조금 오래 걸리는 적재마다 '시간 초과' 라고 적혀 실패처럼 보였다.
 */
const RUN_WATCH_LIMIT_MS = 2 * 60 * 60 * 1000;

/**
 * 지금 연 워크플로에 대한 동작 — 되돌리기·다시하기, Save, 버전, 실행 이력, DAG 코드, Deploy, 실행(·중지).
 *
 * 캔버스 오른쪽 위 도구 모음에 파일 동작(children: 내려받기·불러오기·그림·PDF)과 **같은 아이콘 모양으로** 함께 놓는다.
 * 예전에는 캔버스 위 한 줄(ModernLayout 안)에 글자 버튼으로 있었는데, 레이아웃 안에서 정의된 컴포넌트라
 * 레이아웃이 다시 그려질 때마다(노드 속성 위치 전환 등) 새로 마운트되어 배포·실행 상태를 잃을 수 있었다 — 그래서 파일로 뺐다.
 * 대화상자는 캔버스 안에 가두지 않도록 document.body 로 띄운다.
 */
const WorkflowActions: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  // Save → QBase (OPENFLOW_WORKFLOW_VER 에 새 버전 행 + 헤더 CUR_VER_NO)
  const currentId = useSelector((state: RootState) => state.workflow.current?.id);
  const currentName = useSelector((state: RootState) => state.workflow.current?.name);
  const storeNodes = useSelector((state: RootState) => state.workflow.nodes);
  const storeEdges = useSelector((state: RootState) => state.workflow.edges);
  const saving = useSelector((state: RootState) => state.project.saving);
  const curVerNo = useSelector((state: RootState) => state.project.workflows.find(w => w.id === currentId)?.curVerNo ?? 0);
  const usrid = useSelector((state: RootState) => state.auth.user?.usrid || '');
  const savedSnapshot = useSelector((state: RootState) => (currentId ? state.project.savedSnapshots[currentId] : undefined));
  const wfSettings = useSelector((state: RootState) => state.workflow.settings);
  const dirty = !!currentId && isCanvasDirty(savedSnapshot, storeNodes, storeEdges, wfSettings);
  const [paramsOpen, setParamsOpen] = useState(false);
  const viewOnly = useSelector((state: RootState) => state.auth.user?.viewOnly === true);
  // 캔버스 형식 상태 (services/canvasSchema) — 이 빌드보다 새 형식이면 저장·배포·실행을 막는다.
  // 막지 않으면 모르는 필드를 떨어뜨린 채로 덮어써 그 문서를 조용히 망가뜨린다.
  const schemaInfo = useSelector((state: RootState) => (state as any).workflow.schemaInfo as SchemaInfo | null);
  const schemaBlocked = schemaInfo?.tooNew === true;
  // 프로젝트 권한 — 조회자는 보기만 (서버도 막는다, docs/PROJECT_ACCESS.md). 역할을 모르면(예전 저장 경로) 막지 않는다
  const roleViewer = useSelector((state: RootState) => {
    const pid = state.project.workflows.find((w) => w.id === currentId)?.projectId;
    return state.project.projects.find((p) => p.id === pid)?.myRole === 'viewer';
  });
  const locked = viewOnly || schemaBlocked || roleViewer;
  const [versionsOpen, setVersionsOpen] = useState(false);
  /** 과거 버전 복구 — 새 버전으로 저장한 뒤 캔버스를 그 내용으로 교체 */
  const handleRestoreVersion = async (doc: CanvasDoc, verNo: number) => {
    if (!currentId) return;
    const settings = normalizeSettings(doc.settings);
    const r = await dispatch(saveWorkflowCanvas({ id: currentId, nodes: doc.nodes, edges: doc.edges, settings, regUsrid: usrid }));
    if (saveWorkflowCanvas.rejected.match(r)) { showAlert(t('layout:msg.restoreSaveFailed', { message: r.error?.message || '' })); throw new Error('restore failed'); }
    // 캔버스 교체 — 탐색기에서 워크플로를 여는 것과 같은 순서 (히스토리 미들웨어도 로드로 인식)
    dispatch(resetWorkflow());
    dispatch(setCurrentWorkflow({ id: currentId, name: currentName || currentId }));
    dispatch(setWorkflowSettings(settings));
    doc.nodes.forEach((n) => dispatch({ type: 'workflow/addNode', payload: n }));
    doc.edges.forEach((e) => dispatch({ type: 'workflow/addEdge', payload: e }));
    dispatch(requestFitView());
    // 리듀서가 보탠 필드까지 포함해 '저장된 상태' 스냅샷을 맞춘다 (탐색기 feedCanvas 와 같은 이유)
    const w = store.getState().workflow;
    dispatch(setSavedSnapshot({ id: currentId, nodes: w.nodes, edges: w.edges, settings: w.settings }));
  };
  const canUndo = useSelector((state: RootState) => state.workflow.history.past.length > 0);
  const canRedo = useSelector((state: RootState) => state.workflow.history.future.length > 0);

  // 캔버스 언두/리두 단축키 — 입력 중(폼·텍스트영역·SQL 편집기)에는 브라우저 기본 동작을 살린다
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || viewOnly) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) return;
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); dispatch(undo()); }
      else if (k === 'y' || (k === 'z' && e.shiftKey)) { e.preventDefault(); dispatch(redo()); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewOnly]);

  // 예전 형식을 변환해서 열었으면 한 번 알린다. 저장하지 않았으므로 원본은 그대로이고,
  // 사용자가 저장하는 순간 변환 결과가 새 버전이 된다(원본은 이전 버전으로 남는다).
  const notifiedRef = useRef<string>('');
  useEffect(() => {
    if (!currentId || !schemaInfo?.migrated || schemaInfo.tooNew) return;
    const key = `${currentId}:${schemaInfo.docVersion}`;
    if (notifiedRef.current === key) return;
    notifiedRef.current = key;
    showAlert(
      `${t('layout:msg.migratedBody', { from: schemaInfo.docVersion })}\n`
      + schemaInfo.notes.map((n) => `· ${n}`).join('\n')
      + `\n\n${t('layout:msg.migratedSave', { to: CANVAS_SCHEMA_VERSION })}`
      + (schemaInfo.needsClone ? `\n\n${t('layout:msg.migratedClone')}` : ''),
      { title: t('layout:msg.migratedTitle') },
    );
  }, [currentId, schemaInfo]);

  /** 백엔드 _canvas_dag_id 와 같은 규칙 — 배포 직후 응답을 기다리지 않고 실행 URL 을 만들 때 사용 */
  const _canvasDagId = (id: string) => `openflow_${id.toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '')}`;

  /** 이 빌드보다 새 형식이면 쓰기 계열을 모두 막는다 — 저장·배포·실행이 각각 다른 경로로 들어와 한 곳에 모았다 */
  const blockIfSchemaTooNew = (what: 'save' | 'deploy' | 'run'): boolean => {
    if (!schemaBlocked) return false;
    showAlert(
      `${t('layout:msg.tooNewBody', { doc: schemaInfo?.docVersion, build: CANVAS_SCHEMA_VERSION })}\n`
      + t('layout:msg.tooNewAction', { what: t(`layout:action.${what}`) }),
      { title: t('layout:msg.tooNewTitle'), tone: 'warning' },
    );
    return true;
  };

  /**
   * 저장 전 SQL 검증 — SQL 이 바뀐(아직 검증하지 않은) SQL실행 노드가 있으면 검증할지 묻는다.
   * 검증하면 노드마다 요약(cfg.sqlCheck)을 남기고, 문제가 있으면 노드별·문장별로 보여 준 뒤 그래도 저장할지 묻는다.
   * 「검증 없이」 도 그 선택을 노드에 남긴다 — 같은 SQL 로 저장할 때마다 다시 묻지 않게.
   * @returns 저장을 계속할지
   */
  const confirmSqlChecks = async (): Promise<boolean> => {
    const changed = changedSqlNodes(savedSnapshot, storeNodes);
    if (!changed.length) return true;
    const name = (n: any) => n.data?.config?.label || n.data?.label || n.id;
    const pick = await showChoice(t('property:sqlCheck.save.ask', { n: changed.length, names: changed.map(name).join(', ') }), {
      title: t('property:sqlCheck.save.title'), okText: t('property:sqlCheck.save.checkFirst'), altText: t('property:sqlCheck.save.skip'),
    });
    if (pick === null) return false;
    const setCheck = (n: any, sqlCheck: any) => {
      const cur = store.getState().workflow.nodes.find((x: any) => x.id === n.id) || n;
      dispatch(updateNodeData({ id: n.id, data: { config: { ...(cur.data?.config || {}), sqlCheck } } } as any));
    };
    if (pick === 'alt') {
      changed.forEach((n) => setCheck(n, skippedCheck(n.data.config.connection, n.data.config.sql)));
      return true;
    }
    await pushPcConnsFor(changed.map((n) => n.data.config.connection));
    const outcomes: Array<{ n: any; r?: CheckResult; err?: string }> = [];
    for (const n of changed) {
      const cfg = n.data.config;
      try {
        const r = await runSqlCheck({ connection: cfg.connection, sql: cfg.sql, params: cfg.params || [], workflowId: currentId, nodeId: n.id, nodeLabel: name(n), trigger: 'save' });
        rememberResult(n.id, cfg.connection, cfg.sql, r);
        setCheck(n, toNodeCheck(cfg.connection, cfg.sql, r));
        outcomes.push({ n, r });
      } catch (e) {
        outcomes.push({ n, err: (e as Error).message || String(e) });
      }
    }
    const bad = outcomes.filter((o) => o.err || (o.r && (o.r.summary.error || o.r.summary.warn)));
    if (!bad.length) return true;
    const body = (
      <div>
        <div className="mb-2">{t('property:sqlCheck.save.issues')}</div>
        <div className="max-h-64 overflow-auto text-xs space-y-1.5">
          {bad.map(({ n, r, err }) => {
            const issues = (r?.statements || []).filter((s) => s.status === 'error' || s.status === 'warn');
            return (
              <div key={n.id} className="border border-gray-200 rounded px-2 py-1">
                <div className="font-semibold text-gray-800">
                  {err ? t('property:sqlCheck.save.nodeFailed', { name: name(n), message: err }) : t('property:sqlCheck.save.nodeLine', { name: name(n), error: r!.summary.error, warn: r!.summary.warn })}
                </div>
                {issues.slice(0, 3).map((s) => (
                  <div key={s.index} className={`font-mono break-words ${s.status === 'error' ? 'text-red-700' : 'text-amber-800'}`}>
                    {t('property:sqlCheck.save.stmtLine', { index: s.index, line: s.errorLine || s.line, message: s.message || t(`property:sqlCheck.reason.${s.code}`, s.params || {}) })}
                  </div>
                ))}
                {issues.length > 3 && <div className="text-gray-500">{t('property:sqlCheck.save.more', { n: issues.length - 3 })}</div>}
              </div>
            );
          })}
        </div>
        <div className="mt-2 text-xs text-gray-500">{t('property:sqlCheck.save.hint')}</div>
      </div>
    );
    const ok = await showConfirm(body, { title: t('property:sqlCheck.save.title'), okText: t('property:sqlCheck.save.anyway'), cancelText: t('property:sqlCheck.save.fix') });
    if (!ok) {
      // 고치기 — 첫 노드를 골라 그 SQL 편집기를 검증 결과와 함께 연다(속성 패널이 그려진 뒤)
      const first = bad[0].n;
      dispatch(setSelectedNode(first.id));
      setTimeout(() => window.dispatchEvent(new CustomEvent(OPEN_SQL_CHECK_EVENT, { detail: { nodeId: first.id, pane: 'results' } })), 150);
    }
    return ok;
  };

  const handleSave = async (): Promise<boolean> => {
    if (!currentId) { showAlert(t('layout:msg.selectToSave')); return false; }
    if (blockIfSchemaTooNew('save')) return false;
    // 마지막 저장과 같으면 새 버전을 만들지 않는다 — 버전 이력이 같은 내용으로 채워지면 되돌릴 버전을 찾기 어렵다.
    // 판정은 헤더의 ● 표시와 같은 기준(정의가 주인인 칸을 걷은 캔버스 비교)이다.
    if (!dirty) {
      showAlert(t('layout:msg.noChanges', { ver: curVerNo }), { title: t('common:ui.save') });
      return true;
    }
    // 내용이 있던 워크플로를 빈 캔버스로 덮는 것은 대개 실수다 — 열자마자 저장했거나, 캔버스가 아직
    // 올라오지 않은 상태다. 되돌릴 수는 있지만(이전 버전) 그 사실을 아는 사람이 드물어 먼저 묻는다.
    if (storeNodes.length === 0 && storeEdges.length === 0 && curVerNo >= 1) {
      const ok = await showConfirm(t('layout:msg.saveEmpty', { ver: curVerNo }),
        { title: t('layout:msg.saveEmptyTitle'), okText: t('layout:msg.saveEmptyOk'), tone: 'warning' });
      if (!ok) return false;
    }
    if (!(await confirmSqlChecks())) return false;
    // 검증 요약을 노드에 방금 넣었을 수 있다 — 저장은 지금 스토어의 노드로
    const nodesNow = store.getState().workflow.nodes;
    const r = await dispatch(saveWorkflowCanvas({ id: currentId, nodes: nodesNow, edges: storeEdges, settings: wfSettings, regUsrid: usrid }));
    if (saveWorkflowCanvas.rejected.match(r)) {
      showAlert(t('layout:msg.saveFailed', { message: r.error?.message || '' }));
      return false;
    }
    return true;
  };

  // 배포: 캔버스 → DAG 파일. 실행: 배포된 DAG 트리거 후 완료까지 상태 폴링
  const [deployedDagId, setDeployedDagId] = useState<string | null>(null);
  const [deployedTaskMap, setDeployedTaskMap] = useState<Record<string, string>>({});
  /** 방금 배포가 돌려준 대응표 — 실행을 지켜보는 루프는 배포 **전에 만들어진** 함수 안에서 돌아 state 의 새 값을 보지 못한다.
   *  예전 대응표로 그리면 실행 노드를 못 찾아 「앞이 끝났으니 완료」 로 추정했고, 최종 실행을 맞추는 쪽(RunStatusSync)은
   *  「대기/실행 중」 으로 그려 둘이 번갈아 보였다(현장 2026-10-12: 도는 실행 노드가 실행완료↔실행대기로 깜박임) */
  const taskMapRef = useRef<Record<string, string>>({});
  taskMapRef.current = deployedTaskMap;
  const [deployedSnapshot, setDeployedSnapshot] = useState<string | null>(null); // 배포 시점 캔버스 — 바뀌었으면 실행 전 재배포
  const [history, setHistory] = useState<{ open: boolean; focusNodeId: string | null; taskMap: Record<string, string> }>({ open: false, focusNodeId: null, taskMap: {} });
  /** 실행 이력 열기 — 배포 응답의 task_map 이 없으면(새로 열었을 때) 미리보기 변환으로 노드→태스크 매핑을 구한다 */
  const openHistory = async (focusNodeId: string | null = null) => {
    if (!currentId) { showAlert(t('layout:msg.selectWorkflow')); return; }
    let map = deployedTaskMap;
    if (!map || Object.keys(map).length === 0) {
      try {
        const res = await be1Fetch(`/api/canvas/preview`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ workflow_id: currentId, name: currentName, nodes: storeNodes, edges: storeEdges, schedule_interval: wfSettings.schedule, settings: withTimeZone(wfSettings) }) });
        const body = await res.json();
        if (res.ok) map = body.task_map || {};
      } catch { /* 매핑 없이도 이력은 보인다 */ }
    }
    setHistory({ open: true, focusNodeId, taskMap: map || {} });
  };
  // 캔버스 노드의 실행 상태 뱃지 클릭 → 그 노드의 최신 로그
  useEffect(() => {
    const h = (e: Event) => openHistory((e as CustomEvent).detail?.nodeId ?? null);
    window.addEventListener('openflow:open-node-log', h);
    return () => window.removeEventListener('openflow:open-node-log', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, currentName, storeNodes, storeEdges, deployedTaskMap]);
  const [codeView, setCodeView] = useState<{ dagId: string; file?: string; code: string; deployed: boolean; override: DagOverrideInfo | null; engine?: 'local' | 'airflow' } | null>(null);
  /** 이 워크플로의 DAG 코드 수정본 — 켜져 있으면 헤더에 표시하고, 캔버스가 바뀌었으면 실행 전에 묻는다 */
  const [overrideInfo, setOverrideInfo] = useState<DagOverrideInfo | null>(null);
  const canvasBody = () => ({ workflow_id: currentId, name: currentName, nodes: storeNodes, edges: storeEdges, schedule_interval: wfSettings.schedule, settings: withTimeZone(wfSettings) });
  /** 서버에 수정본 상태를 묻는다(변환 미리보기에 함께 온다). 변환이 안 되는 캔버스면 조용히 넘어간다 */
  const fetchOverride = async (): Promise<DagOverrideInfo | null> => {
    if (!currentId) return null;
    try {
      const res = await be1Fetch(`/api/canvas/preview`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(canvasBody()) }, { retries: 0 });
      if (!res.ok) return overrideInfo;
      const o = (await res.json()).override || null;
      setOverrideInfo(o);
      return o;
    } catch { return overrideInfo; }
  };
  useEffect(() => { setOverrideInfo(null); if (currentId) fetchOverride(); /* 워크플로를 열 때 한 번 */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  // DAG 코드 보기: 배포 없이 현재 캔버스를 변환만 해서 모달로 표시 (변환 오류도 여기서 확인)
  const handleShowCode = async () => {
    if (!currentId) { showAlert(t('layout:msg.selectWorkflow')); return; }
    try {
      const res = await be1Fetch(`/api/canvas/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workflow_id: currentId, name: currentName, nodes: storeNodes, edges: storeEdges, schedule_interval: wfSettings.schedule, settings: withTimeZone(wfSettings) }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(errorText(body, res.statusText));
      setOverrideInfo(body.override || null);
      setCodeView({ dagId: body.dag_id, file: body.file, code: body.code, deployed: deployedDagId === body.dag_id, override: body.override || null, engine: body.engine });
    } catch (e) {
      showAlert(t('layout:msg.convertFailed', { message: (e as Error).message }));
    }
  };
  /** wf: 이 글이 어느 워크플로의 것인가 — 다른 워크플로로 넘어가면 보이지 않는다(예전에는 앞 워크플로의 실행 결과가 계속 떠 있었다) */
  // 실행 상태는 **워크플로마다** 따로 둔다. 예전에는 화면에 하나뿐이라, 한 워크플로가 도는 동안 어느 워크플로를 열어도
  // 실행 중(회전 아이콘·중지 버튼)으로 보였고 다른 워크플로를 함께 실행할 수도 없었다(현장 2026-10-12).
  type RunState = { phase: 'idle' | 'deploying' | 'running' | 'done' | 'error'; text: string; wf?: string | null };
  const [runStates, setRunStates] = useState<Record<string, RunState>>({});
  const runState: RunState = runStates[String(currentId ?? '')] || { phase: 'idle', text: '', wf: currentId };
  /** 상태에 적힌 워크플로(wf)의 칸에 넣는다 — 실행을 지켜보는 동안 다른 워크플로로 넘어가도 제 칸에 들어간다 */
  const setRunState = (st: RunState) => setRunStates((m) => ({ ...m, [String(st.wf ?? '')]: st }));
  /** 지금 열려 있는 워크플로 — 실행을 지켜보는 동안 다른 워크플로로 넘어갔는지 본다 */
  const currentIdRef = useRef(currentId);
  currentIdRef.current = currentId;

  /**
   * 실행 상태 한 줄 요약 — 태스크 id 나열(`p0_cript4:success, p1_cript5:success …`) 대신 **노드 이름**으로, 셀 것은 센다.
   *   도는 중  완료 3/10 · 실행 중: P0파티션적재, P1파티션적재 외 2
   *   성공     노드 10개
   *   실패     실패: P3파티션적재 · 성공 7/10
   */
  const summarizeRun = (tasks: any[], taskMap: Record<string, string>, nodes: any[]): string => {
    const nodeOfTask = new Map(Object.entries(taskMap).map(([nodeId, taskId]) => [taskId, nodeId]));
    const nameOf = (ti: any) => {
      const tid = String(ti.task_id || '');
      // 방금 배포한 실행은 대응표(task_map)가 아직 앞의 것일 수 있다 — 그때는 태스크 id 끝의 노드 id 로 찾는다(`<종류>_<노드id>`)
      const n = nodes.find((x: any) => x.id === nodeOfTask.get(tid))
        || nodes.find((x: any) => tid.toLowerCase().endsWith('_' + String(x.id).toLowerCase().replace(/[^a-z0-9_]+/g, '_')));
      return String(n?.data?.label || tid);
    };
    const pick = (list: any[]) => {
      const names = list.slice(0, 3).map(nameOf).join(', ');
      return list.length > 3 ? t('layout:run.sumMore', { names, n: formatNumber(list.length - 3) }) : names;
    };
    const mine = tasks.filter((x) => !x.out_of_scope && !x.reused_from);
    const of = (...states: string[]) => mine.filter((x) => states.includes(x.state));
    const failed = of('failed');
    const running = of('running');
    const ok = of('success').length;
    const total = formatNumber(mine.length);
    const parts: string[] = [];
    if (failed.length) parts.push(t('layout:run.sumFailed', { names: pick(failed) }));
    if (running.length) parts.push(t('layout:run.sumRunning', { names: pick(running) }));
    if (failed.length || running.length || ok < mine.length) parts.push(t('layout:run.sumCounts', { ok: formatNumber(ok), n: total }));
    else parts.push(t('layout:run.sumAll', { n: total }));
    return parts.join(' · ');
  };
  /** 지금 도는 실행 — 중지 버튼이 이것을 끊는다 (실행이 끝나면 비운다) */
  const [runInfos, setRunInfos] = useState<Record<string, { dagId: string; runId: string } | null>>({});
  const runInfo = runInfos[String(currentId ?? '')] || null;
  /** 이 함수가 만들어진 때의 워크플로(= 실행을 시작한 워크플로)의 칸에 넣는다 */
  const setRunInfo = (v: { dagId: string; runId: string } | null) => setRunInfos((m) => ({ ...m, [String(currentId ?? '')]: v }));
  const [stopping, setStopping] = useState(false);
  /**
   * 중지를 요청한 실행 — 서버가 도는 작업을 끊고 실행을 「중지됨」 으로 닫을 때까지 잠깐 「실행 중」 이다.
   * 예전에는 중지를 누르자마자 중지 대상(runInfo)을 비워서, 지켜보기가 다시 「실행 중」 을 칠하면 **도는 표시는 있는데 중지 버튼이 없었다**
   * (2026-10-30 현장). 이제 끝날 때까지 대상을 두고 「중지 요청됨」 으로 보인다.
   */
  const stopRequestedRef = useRef<Set<string>>(new Set());

  const handleDeploy = async (): Promise<boolean> => {
    if (!currentId) { showAlert(t('layout:msg.selectToDeploy')); return false; }
    if (blockIfSchemaTooNew('deploy')) return false;
    setRunState({ wf: currentId, phase: 'deploying', text: t('layout:msg.deploying') });
    const startedAt = Date.now();
    try {
      const res = await be1Fetch(`/api/canvas/deploy`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workflow_id: currentId, name: currentName, nodes: storeNodes, edges: storeEdges, schedule_interval: wfSettings.schedule, settings: withTimeZone(wfSettings) }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(errorText(body, res.statusText));
      setDeployedDagId(body.dag_id);
      setDeployedTaskMap(body.task_map || {});
      taskMapRef.current = body.task_map || {};      // 이어지는 실행 루프가 바로 쓴다
      setDeployedSnapshot(normalizeCanvas(storeNodes, storeEdges, wfSettings));
      dispatch(clearRunStatus());
      setCodeView(v => (v ? { ...v, deployed: true } : v));
      setRunState({ wf: currentId, phase: 'idle', text: t('layout:msg.deployed', { dagId: body.dag_id }) });
      // 막지는 않지만 실행 전에 알린다 — 이 서버에 없는 연결, 읽을 파일이 없음, 쓸 곳이 서버 폴더 밖 (서버 services/migrate_checks)
      const warns: Array<{ code: string; block: string; params: Record<string, string> }> = body.warnings || [];
      if (warns.length) {
        showAlert(warns.map((w) => `· ${t(`layout:migrate.report.${w.code}`, { block: w.block, ...w.params, defaultValue: w.code })}`).join('\n'),
          { title: t('layout:msg.deployWarnTitle'), tone: 'warning' });
      }
      logUsage('workflow.deploy', { target: currentId, durationMs: Date.now() - startedAt, detail: { dagId: body.dag_id } });
      return true;
    } catch (e) {
      setRunState({ wf: currentId, phase: 'error', text: t('layout:msg.deployFailed', { message: (e as Error).message }) });
      logError('workflow.deploy', e, { target: currentId, where: 'deploy' });
      return false;
    }
  };

  /** 파라미터 팝업이 떠 있는 동안 기다리는 부분 실행 범위 (전체 실행이면 null) */
  const [pendingScope, setPendingScope] = useState<RunScope | null>(null);
  /** 실행 버튼: 캔버스가 ${변수} 를 참조하면 파라미터 입력 팝업을 먼저 띄운다. scope 가 있으면 부분 실행 */
  const handleRunClick = async (scope: RunScope | null = null) => {
    if (!currentId) { showAlert(t('layout:msg.selectToRun')); return; }
    if (blockIfSchemaTooNew('run')) return;
    if (runState.phase === 'running' || runState.phase === 'deploying') { showAlert(t('layout:msg.alreadyRunning')); return; }
    if (scope && !(await confirmScope(scope))) return;
    if (extractParamRefs(storeNodes, wfSettings).length > 0) { setPendingScope(scope); setParamsOpen(true); } else handleRun({}, scope);
  };
  /** 부분 실행 확인 — 무엇을 돌리고 앞 노드의 결과를 어떻게 구하는지 먼저 알린다 */
  const confirmScope = (scope: RunScope) => {
    const names = scope.nodes.map((id) => storeNodes.find((n: any) => n.id === id)?.data?.label || id);
    const joined = names.join(t('layout:scope.nameSeparator'));
    const what = scope.mode === 'from' ? t('layout:scope.fromNodes', { names: joined }) : t('layout:scope.onlyNodes', { names: joined });
    return showConfirm(
      t('layout:scope.confirmBody', { what }),
      { title: scope.mode === 'from' ? t('layout:scope.fromTitle') : t('layout:scope.onlyTitle'), okText: t('layout:scope.runOk') },
    );
  };
  // 캔버스 노드 오른쪽 클릭 메뉴(여기부터 실행 / 이 노드만 실행)
  const runClickRef = useRef(handleRunClick);
  runClickRef.current = handleRunClick;
  useEffect(() => {
    const h = (e: Event) => { const d = (e as CustomEvent).detail; if (d?.nodes?.length) runClickRef.current({ mode: d.mode, nodes: d.nodes }); };
    window.addEventListener('openflow:run-scope', h);
    return () => window.removeEventListener('openflow:run-scope', h);
  }, []);
  const handleRun = async (conf: Record<string, string>, scope: RunScope | null = null) => {
    if (!currentId) { showAlert(t('layout:msg.selectToRun')); return; }
    // 1) 미저장 변경이 있으면 저장할지 묻고, 저장한 뒤 진행 (취소하면 실행하지 않는다)
    if (dirty) {
      if (!(await showConfirm(t('layout:run.unsavedConfirm'), { title: t('layout:run.title'), okText: t('layout:run.saveAndRun') }))) return;
      if (!(await handleSave())) return;
    }
    // 1-1) DAG 코드 수정본을 켜 두었는데 그 뒤 캔버스가 바뀌었으면 — 캔버스 변경은 수정본에 없으니 묻는다
    if (overrideInfo?.active) {
      const o = await fetchOverride();
      if (o?.active && o.stale && !(await showConfirm(
        t('layout:run.overrideStale'),
        { title: t('layout:run.overrideTitle'), okText: t('layout:run.overrideTitle'), tone: 'warning' },
      ))) return;
    }
    // 2) 배포가 없거나 배포 이후 캔버스가 바뀌었으면 자동 재배포
    let dagId = deployedDagId;
    // 마지막으로 배포한 것이 **이 워크플로**가 아니면 다시 배포한다 — 워크플로를 오가며 실행할 때 앞 워크플로의 배포를
    // 이 워크플로의 것으로 잘못 쓰지 않게(복제한 워크플로는 캔버스가 같아 스냅샷 비교만으로는 구분되지 않는다)
    if (!dagId || dagId !== _canvasDagId(currentId) || deployedSnapshot !== normalizeCanvas(storeNodes, storeEdges, wfSettings)) {
      if (!(await handleDeploy())) return;
      dagId = _canvasDagId(currentId);
    }
    // 2-1) 「내 PC 에만 저장」 연결 — 이 PC 금고의 값을 지금 서버로 보낸다(실행 동안만 쓰고 지운다, services/pcConnections)
    const pcMissing = await pushPcConnsFor(connIdsOf(storeNodes));
    if (pcMissing.length) {
      setRunState({ wf: currentId, phase: 'error', text: t('layout:run.pcMissing', { conns: pcMissing.join(', ') }) });
      return;
    }
    const scopeText = scope ? (scope.mode === 'from' ? t('layout:scope.fromPrefix') : t('layout:scope.onlyPrefix')) : '';
    setRunState({ wf: currentId, phase: 'running', text: t('layout:run.waitingTrigger', { prefix: scopeText }) });
    try {
      // 부분 실행은 범위를 정할 캔버스를 함께 보낸다 — QBase 에 저장하는 배포는 서버 저장소에 캔버스가 없다
      const res = await be1Fetch(`/api/canvas/${dagId}/run`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        // 전체 실행도 노드를 함께 보낸다 — 서버가 「내 PC」 연결을 찾는 데 쓴다(저장소가 QBase 면 서버에 캔버스가 없다)
        body: JSON.stringify(scope ? { conf, scope, name: currentName, nodes: storeNodes, edges: storeEdges, settings: wfSettings } : { conf, nodes: storeNodes }) });
      const body = await res.json();
      if (!res.ok) throw new Error(errorText(body, res.statusText));
      const runId: string = body.dag_run_id;
      // 부분 실행을 모르는 이전 서버는 scope 를 버리고 전체를 돌린다 — 응답에 범위가 없으면 그 사실을 알린다
      if (scope && !body.scope) showAlert(t('layout:scope.unsupported'), { title: t('layout:scope.unsupportedTitle'), tone: 'warning' });
      setRunInfo({ dagId: String(dagId), runId });      // 중지 버튼이 끊을 대상
      // 이 실행의 대응표 — 지켜보는 동안 다른 워크플로를 배포·실행하면 taskMapRef 는 그쪽 것으로 바뀐다
      const runTaskMap = taskMapRef.current;
      dispatch(setRunStatus(computeNodeRunStatus(storeNodes, storeEdges, runTaskMap, [], null)));
      const started = Date.now();
      // 서버가 '끝났다' 고 할 때까지 본다. 예전에는 3분이 지나면 보기를 그만두고 '시간 초과' 라고 적었는데,
      // 실행은 서버에서 그대로 돌고 있었다 — 오래 걸리는 적재가 실패한 것처럼 보였고, 기록도 남지 않았다.
      // 그래도 영원히 돌지는 않게 상한을 둔다(사람이 잊고 떠난 탭이 하루 종일 서버를 두드리지 않도록).
      while (Date.now() - started < RUN_WATCH_LIMIT_MS) {
        await new Promise(r => setTimeout(r, 3000));
        const st = await (await be1Fetch(`/api/canvas/${dagId}/runs/${encodeURIComponent(runId)}`)).json();
        const tasks = (st.tasks || []).map((ti: any) => `${ti.task_id}:${ti.state || '-'}`).join(', ');      // 기록용(id 그대로)
        const summary = summarizeRun(st.tasks || [], runTaskMap, storeNodes);                              // 화면용(이름·요약)
        // 지켜보는 동안 다른 워크플로로 넘어갔으면 그 캔버스에 이 실행의 상태를 칠하지 않는다
        if (currentIdRef.current === currentId) {
          dispatch(setRunStatus(computeNodeRunStatus(storeNodes, storeEdges, runTaskMap, st.tasks || [], st.state)));
          dispatch(setRunDurations(computeNodeDurations(runTaskMap, st.tasks || [])));
        }
        setRunState({ wf: currentId, phase: 'running', text: stopRequestedRef.current.has(runId)
          ? t('layout:run.stopRequested', { prefix: scopeText, tasks: summary })
          : t('layout:run.progress', { prefix: scopeText, tasks: summary }) });
        if (st.state === 'success' || st.state === 'failed' || st.state === 'stopped') {
          setRunInfo(null);                 // 끝났으니 중지할 대상이 없다
          stopRequestedRef.current.delete(runId);
          // 중지는 실패가 아니다 — 빨간 글씨로 '실패' 라고 적으면 사용자가 멈춘 것을 오류로 읽는다
          setRunState(st.state === 'stopped'
            ? { wf: currentId, phase: 'done', text: t('layout:run.stopped', { prefix: scopeText }) }
            : { wf: currentId, phase: st.state === 'success' ? 'done' : 'error', text: t(st.state === 'success' ? 'layout:run.succeeded' : 'layout:run.failedWith', { prefix: scopeText, tasks: summary }) });
          // 실행은 기록에 남는다 — 실패했는데 사용·오류 로그에 아무것도 없으면 나중에 되짚을 수가 없다
          const detail = { dagId: String(dagId), runId, state: st.state, scope: scope?.mode || 'all', tasks };
          if (st.state === 'failed') logError('workflow.run', new Error(t('layout:run.logFailed', { tasks })), { target: currentId, where: 'run', detail });
          else logUsage('workflow.run', { target: currentId, durationMs: Date.now() - started, detail });
          // 최종 상태·소요시간을 서버 기준으로 다시 맞춘다 (방금 배포했으면 위의 task_map 이 아직 이전 값일 수 있다)
          if (currentIdRef.current === currentId) window.dispatchEvent(new CustomEvent('openflow:run-finished'));
          return;
        }
      }
      // 상한에 닿았다 — **실행이 멈춘 것이 아니라 보기를 그만두는 것**이다. 그렇게 적고 기록도 남긴다.
      setRunState({ wf: currentId, phase: 'error', text: t('layout:run.watchGaveUp', { minutes: Math.round(RUN_WATCH_LIMIT_MS / 60000) }) });
      logError('workflow.run', new Error(t('layout:run.watchGaveUp', { minutes: Math.round(RUN_WATCH_LIMIT_MS / 60000) })),
        { target: currentId, where: 'run.watch', detail: { dagId: String(dagId), runId } });
    } catch (e) {
      setRunState({ wf: currentId, phase: 'error', text: t('layout:run.failed', { message: (e as Error).message }) });
      logError('workflow.run', e, { target: currentId, where: 'run' });
    }
  };

  const busy = runState.phase === 'running' || runState.phase === 'deploying';
  // 툴팁은 버튼 이름만 — 예전 글자 버튼에 적혀 있던 그대로(부연 설명은 싣지 않는다)

  return (
    <>
      <div className="pointer-events-auto flex flex-wrap items-center justify-end gap-1 bg-white rounded-lg shadow-lg p-2 max-w-full">
        {schemaBlocked && (
          <span className="p-2 text-red-600" title={t('layout:bar.readOnly', { doc: schemaInfo?.docVersion })}>
            {/* @ts-ignore react-icons IconType 과 React 18 타입 불일치 회피 */}
            <FiLock size={22} />
          </span>
        )}
        {viewOnly && (
          <ToolbarButton
            icon={FiEye}
            className="text-amber-600"
            active
            title={t('layout:bar.viewOnly')}
            onClick={async () => {
              const r = await dispatch(acquireEdit());
              if (acquireEdit.fulfilled.match(r) && !r.payload) showAlert(t('layout:bar.stillEditing'));
            }}
          />
        )}
        {roleViewer && <ToolbarButton icon={FiLock} className="text-gray-500" active title={t('layout:bar.roleViewer')} onClick={() => showAlert(t('layout:bar.roleViewerHint'))} />}
        <ToolbarButton point="toolbar:undo" icon={FiCornerUpLeft} className="text-gray-600" onClick={() => dispatch(undo())} disabled={!canUndo || locked} title={t('layout:bar.undo')} />
        <ToolbarButton point="toolbar:redo" icon={FiCornerUpRight} className="text-gray-600" onClick={() => dispatch(redo())} disabled={!canRedo || locked} title={t('layout:bar.redo')} />
        <ToolbarDivider />
        <ToolbarButton
          point="toolbar:save"
          icon={saving ? FiLoader : FiSave}
          spin={saving}
          className="text-blue-600"
          onClick={handleSave}
          // 실행 중에는 저장하지 못한다(서버 services/run_guard 가 판정한다 — 다른 탭·다른 사람의 실행도 거기서 막힌다).
          // 이 화면에서 시작한 실행은 버튼부터 잠가 이유를 보여 준다. 편집은 그대로 할 수 있다
          disabled={saving || !currentId || locked || runState.phase === 'running'}
          title={runState.phase === 'running' ? t('layout:bar.saveWhileRunning')
            : saving ? t('common:ui.saving') : `${t('common:ui.save')}${curVerNo ? ` (v${curVerNo})` : ''}${dirty ? ' ●' : ''}`}
          badge={currentId && (curVerNo > 0 || dirty) ? (
            <span className={`block text-[0.5625rem] leading-[13px] px-1 rounded-full ${dirty ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
              {curVerNo ? `v${curVerNo}` : ''}{dirty ? '●' : ''}
            </span>
          ) : null}
        />
        <ToolbarButton point="toolbar:versions" icon={FiLayers} className="text-gray-600" onClick={() => setVersionsOpen(true)} disabled={!currentId || curVerNo < 1} title={t('layout:bar.versions')} />
        <ToolbarButton point="toolbar:history" icon={FiActivity} className="text-gray-600" onClick={() => openHistory(null)} disabled={!currentId} title={t('layout:bar.history')} />
        <ToolbarDivider />
        <ToolbarButton
          point="toolbar:code"
          icon={FiCode}
          className="text-gray-600"
          onClick={handleShowCode}
          disabled={!currentId}
          title={overrideInfo?.active ? `${t('layout:bar.code')} (${t('layout:bar.codeOverrideBadge')})` : t('layout:bar.code')}
          badge={overrideInfo?.active ? <span className="block w-2.5 h-2.5 rounded-full bg-amber-500 ring-2 ring-white" /> : null}
        />
        <ToolbarButton
          point="toolbar:deploy"
          icon={runState.phase === 'deploying' ? FiLoader : FiUploadCloud}
          spin={runState.phase === 'deploying'}
          className="text-indigo-600"
          onClick={handleDeploy}
          disabled={!currentId || busy || roleViewer}
          title={runState.phase === 'deploying' ? t('common:ui.deploying') : t('common:ui.deploy')}
        />
        <ToolbarButton
          point="toolbar:run"
          icon={runState.phase === 'running' ? FiLoader : FiPlay}
          spin={runState.phase === 'running'}
          className="text-green-600"
          onClick={() => handleRunClick()}
          disabled={!currentId || busy || roleViewer}
          title={runState.phase === 'running' ? t('common:ui.running') : t('common:ui.run')}
        />
        {/* 즉시 중지 — 도는 태스크를 끊는다. 실행 중일 때만 보인다 */}
        {runInfo && runState.phase === 'running' && (
          <ToolbarButton
            point="toolbar:stop"
            icon={FiSquare}
            className="text-red-600"
            disabled={stopping || roleViewer || stopRequestedRef.current.has(runInfo.runId)}
            title={stopping || stopRequestedRef.current.has(runInfo.runId) ? t('layout:bar.stopping') : t('layout:bar.stop')}
            onClick={async () => {
              if (!(await showConfirm(t('layout:bar.stopConfirm'), { title: t('layout:bar.stopConfirmTitle'), okText: t('layout:bar.stopOk') }))) return;
              setStopping(true);
              try {
                await stopRun(currentId!, runInfo.runId);
                // 대상은 실행이 실제로 끝날 때까지 둔다 — 지켜보기가 끝(stopped/failed)을 받으면 비운다
                stopRequestedRef.current.add(runInfo.runId);
                setRunState({ wf: currentId, phase: 'running', text: t('layout:run.stopRequested', { prefix: '', tasks: '' }) });
              } catch (e) {
                showAlert(t('layout:bar.stopFailed', { message: (e as Error).message }));
              } finally {
                setStopping(false);
              }
            }}
          />
        )}
        {children && <ToolbarDivider />}
        {children}
      </div>
      {/* 배포·실행 진행 상황 — 도구 모음 바로 아래 한 줄 */}
      {runState.text && runState.wf === currentId && (
        <div
          className={`pointer-events-auto max-w-full truncate text-xs px-2 py-1 rounded bg-white shadow ${runState.phase === 'error' ? 'text-red-600' : runState.phase === 'done' ? 'text-green-600' : 'text-gray-600'}`}
          title={runState.text}
        >
          {runState.text}
        </div>
      )}
      {createPortal(
        <>
          {paramsOpen && <RunParamsDialog refs={extractParamRefs(storeNodes, wfSettings)} onClose={() => { setParamsOpen(false); setPendingScope(null); }} onRun={(conf) => { setParamsOpen(false); handleRun(conf, pendingScope); setPendingScope(null); }} />}
          {versionsOpen && currentId && (
            <VersionHistoryDialog workflowId={currentId} workflowName={currentName || currentId} curVerNo={curVerNo}
              onClose={() => setVersionsOpen(false)} onRestore={handleRestoreVersion} />
          )}
          {history.open && currentId && (
            <RunHistoryDialog dagId={_canvasDagId(currentId)} workflowName={currentName || currentId} taskMap={history.taskMap}
              focusNodeId={history.focusNodeId} onClose={() => setHistory(h => ({ ...h, open: false }))} />
          )}
          {codeView && (
            <DagCodeModal dagId={codeView.dagId} file={codeView.file} code={codeView.code} deployed={codeView.deployed}
              override={codeView.override} engine={codeView.engine} canvasBody={canvasBody()}
              onClose={() => setCodeView(null)}
              onOverrideChanged={(o) => { setOverrideInfo(o); setDeployedSnapshot(null); /* 다음 실행에서 다시 배포 */ }}
              onRun={() => handleRunClick()} />
          )}
        </>,
        document.body,
      )}
    </>
  );
};

export default WorkflowActions;
